import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import PublicShell from "@/components/PublicShell";
import { SignGrid, ModeToggle } from "@/components/SignGrid";
import { READINGS } from "@/lib/readingsContent";
import { api } from "@/lib/apiClient";
import { cardCls } from "@/lib/creators";

function Avatar({ c }) {
  if (c.avatarUrl) return <img src={`${process.env.REACT_APP_BACKEND_URL}${c.avatarUrl}`} alt={c.displayName} data-testid="storefront-avatar" className="w-20 h-20 rounded-full object-cover border-2 border-[#D4AF37]/60" />;
  return <span data-testid="storefront-avatar" className="w-20 h-20 rounded-full bg-[#D4AF37]/15 border-2 border-[#D4AF37]/60 flex items-center justify-center text-3xl gold-text">{c.displayName[0]}</span>;
}

export default function CreatorStorefront() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [missing, setMissing] = useState(false);
  const [mode, setMode] = useState("daily");
  useEffect(() => {
    api.post("/creators/track", { handle, landing: `/c/${handle}` })
      .then(({ data }) => (data.creator ? setC(data.creator) : setMissing(true))).catch(() => setMissing(true));
  }, [handle]);
  useEffect(() => {
    if (c) document.title = `${c.displayName}'s Daily Horoscope · Powered by Astra`;
  }, [c]);

  if (missing) return (
    <PublicShell testId="storefront-not-found">
      <div className="px-6 py-24 text-center"><h1 className="text-3xl font-light mb-2">Page not found</h1>
        <p className="text-[#A7B0C0]">This creator page doesn't exist.</p>
        <Link to="/" className="gold-text underline underline-offset-4 mt-4 inline-block">Today's horoscopes</Link></div>
    </PublicShell>
  );
  if (!c) return <PublicShell testId="storefront-loading"><Loader2 className="animate-spin gold-text mx-auto my-32" size={28} /></PublicShell>;

  const ref = `?ref=${c.refCode}`;
  return (
    <PublicShell testId="creator-storefront">
      <div className="px-5 pt-4 lg:max-w-5xl lg:mx-auto lg:px-10 fade-up">
        <header className="flex items-center gap-4" data-testid="storefront-header">
          <Avatar c={c} />
          <div className="min-w-0">
            <h1 data-testid="storefront-title" className="text-2xl lg:text-4xl font-light leading-tight">{c.displayName}'s Daily Horoscope</h1>
            <p className="text-[11px] uppercase tracking-[0.3em] gold-text mt-1">Powered by Astra</p>
          </div>
        </header>
        {c.bio && <p data-testid="storefront-bio" className="text-base text-[#D6D0C5] mt-4 max-w-2xl">{c.bio}</p>}

        <h2 className="text-lg text-starlight mt-8 mb-3">Choose your star sign</h2>
        <ModeToggle mode={mode} setMode={setMode} />
        <SignGrid onPick={(s) => navigate(`/s/${s.toLowerCase()}/${mode}${ref}`)} className="lg:grid-cols-6 lg:gap-4" />

        <h2 className="text-lg text-starlight mt-10 mb-3">Personal readings</h2>
        <div className="grid gap-3 sm:grid-cols-2" data-testid="storefront-readings">
          {READINGS.map((r) => (
            <Link key={r.slug} to={`/readings/${r.slug}${ref}`} data-testid={`storefront-reading-${r.slug}`} className={`${cardCls} flex items-center justify-between gap-3 hover:border-[#D4AF37]/60 transition-colors`}>
              <span><span className="block text-lg">{r.title}</span><span className="block text-sm text-[#A7B0C0]">{r.short}</span></span>
              <ArrowRight size={16} className="gold-text shrink-0" />
            </Link>
          ))}
        </div>
        <Link to={`/join${ref}`} data-testid="storefront-join" className="block text-center mt-6 border border-[#D4AF37]/40 gold-text text-lg py-3.5 rounded-full hover:bg-[#D4AF37]/10 transition-colors">Get a horoscope for your exact birth chart</Link>
        <p data-testid="storefront-disclosure" className="text-xs text-[#A7B0C0] text-center mt-10">Affiliate link – {c.displayName} may earn commission on purchases made through this page.</p>
      </div>
    </PublicShell>
  );
}
